import React from 'react';
import { appConfigStore, RefundMethod } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { getRefundPopUpInfo } from 'Services/Api/_HumanWritten/RefundService';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';

interface AlterationRefundModalProps {
	transactionId: string;
	// Called once the refund method is decided. `null` means no refund applies (upward/neutral
	// alteration, or an invoiced/unpaid booking) — the caller should just proceed. A method
	// value is sent to the backend and applied to the refund.
	onConfirm: (method: RefundMethod | null) => Promise<void> | void;
	onCancel?: () => void;
}

// Money-back methods come first; credit is the last resort. The list is filtered to the
// methods the acting user's role actually allows.
const METHOD_ORDER: RefundMethod[] = ['Bank', 'Manual', 'Credit'];

const METHOD_META: Record<RefundMethod, { icon: string; title: string; description: string }> = {
	Bank: {
		icon: 'account_balance',
		title: 'Bank refund',
		description: 'Refund to the original payment method',
	},
	Manual: {
		icon: 'credit_card_gear',
		title: 'Manual refund',
		description: 'Processed manually by staff',
	},
	Credit: {
		icon: 'redeem',
		title: 'Credit voucher',
		description: 'Issue a travel credit voucher',
	},
};

/**
 * Shows the alteration refund method modal before a downward-price alteration completes.
 *
 * Behaviour (see #1552):
 * - No refund due (upward/neutral alteration) → resolves immediately with `null`.
 * - Invoiced / unpaid booking → shows an informational "no refund" notice, then resolves `null`.
 * - Credit is the actor's only available method → resolves with `'Credit'` without a modal
 *   (nothing to confirm or choose).
 * - Otherwise → shows the modal listing every method available to the actor, pre-selected to
 *   the tenant default, and resolves with the user's chosen method on confirm.
 */
export const showAlterationRefundModal = async ({
	transactionId,
	onConfirm,
	onCancel,
}: AlterationRefundModalProps): Promise<void> => {
	const data = await getRefundPopUpInfo(transactionId);

	// No refund applies — nothing to confirm.
	if (!data.refundRequired) {
		await onConfirm(null);
		return;
	}

	// A refund may be "required" yet sum to nothing (e.g. a $0 booking, or refund
	// lines that net to zero). There's nothing to refund or choose, so proceed
	// silently without any modal.
	const totalRefund = data.refundMethods.reduce((sum, card) => sum + (card.amountToRefund ?? 0), 0);
	if (totalRefund <= 0) {
		await onConfirm(null);
		return;
	}

	// Invoiced / unpaid bookings don't receive a money refund — show a notice and continue.
	if (data.isInvoiced || data.isUnpaid) {
		await newConfirmModal('No refund required', <p>No refund will be given for this booking.</p>);
		await onConfirm(null);
		return;
	}

	const allowed = appConfigStore.allowedRefundMethodsFor(store.isStaff);
	const availableMethods = METHOD_ORDER.filter(method => allowed.has(method));

	// Config should always yield at least one method, but guard anyway.
	if (availableMethods.length === 0) {
		await onConfirm(null);
		return;
	}

	// Skip the modal only when credit is the sole option — there's nothing to choose, so issue
	// the credit voucher silently.
	if (availableMethods.length === 1 && availableMethods[0] === 'Credit') {
		await onConfirm('Credit');
		return;
	}

	// Pre-select the tenant default when the actor is allowed to use it, else the first option.
	const preferred = appConfigStore.resolveAlterationDefaultRefundMethod(store.isStaff);
	const initialMethod = availableMethods.includes(preferred) ? preferred : availableMethods[0];

	// The confirm button lives in newConfirmModal's chrome, so the live selection is held in a
	// closure that the picker updates and we read once the user confirms.
	const selection = { method: initialMethod };

	const confirmed = await newConfirmModal(
		'Confirm refund method',
		<AlterationRefundPicker
			methods={availableMethods}
			initialMethod={initialMethod}
			totalRefund={totalRefund}
			onSelect={method => {
				selection.method = method;
			}}
		/>,
		{
			cancelText: 'Back',
			confirmText: 'Confirm',
			resolveOnCancel: true,
			resolveOnClose: true,
			shouldCloseOnOverlayClick: false,
		},
	);

	if (!confirmed) {
		onCancel?.();
		return;
	}

	await onConfirm(selection.method);
};

interface AlterationRefundPickerProps {
	methods: RefundMethod[];
	initialMethod: RefundMethod;
	totalRefund: number;
	onSelect: (method: RefundMethod) => void;
}

const AlterationRefundPicker: React.FC<AlterationRefundPickerProps> = ({
	methods,
	initialMethod,
	totalRefund,
	onSelect,
}) => {
	const [selected, setSelected] = React.useState<RefundMethod>(initialMethod);
	const singleMethod = methods.length === 1;

	const choose = (method: RefundMethod) => {
		setSelected(method);
		onSelect(method);
	};

	return (
		<div className="confirm-refund-modal-content">
			<p>
				{singleMethod
					? 'The refund will be processed using the following method.'
					: 'Choose how this refund should be processed.'}
			</p>
			<div className="alteration-refund-picker">
				<p className="input-label">Refund method</p>
				<div className="alteration-refund-picker__options">
					{methods.map(method => {
						const meta = METHOD_META[method];
						const isSelected = selected === method;
						const optionClass = isSelected
							? 'alteration-refund-option alteration-refund-option--selected'
							: 'alteration-refund-option';
						return (
							<button
								key={method}
								type="button"
								className={optionClass}
								onClick={() => choose(method)}
								aria-pressed={isSelected}
							>
								<div className="alteration-refund-option__icon">
									<Icon name={meta.icon} useMaterialIcons />
								</div>
								<div className="alteration-refund-option__content">
									<p className="alteration-refund-option__title">{meta.title}</p>
									<p className="alteration-refund-option__description">{meta.description}</p>
								</div>
								<p className="alteration-refund-option__amount">{formatCurrency(totalRefund)}</p>
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
};
